import { useQuery } from '@tanstack/react-query';
import parse from 'parse-diff';
import { supabaseService } from '@/shared/services/supabaseService';
import { File as ParsedDiffFile } from '@/shared/types/parse-diff';

const fetchDiff = async (
  repoId: string,
  prNumber: number
): Promise<string> => {
  const diff = await supabaseService.getPullRequestDiff(repoId, prNumber);
  if (diff === null) {
    throw new Error('Failed to fetch diff');
  }
  return diff;
};

const cleanAndParseDiff = (rawDiff: string): { files: ParsedDiffFile[], rawDiff: string } => {
  const diffStartIndex = rawDiff.indexOf('diff --git');
  const cleanDiff =
    diffStartIndex > -1 ? rawDiff.substring(diffStartIndex) : rawDiff;

  const parsedFiles = parse(cleanDiff);

  // Aggregate files by path, as `parse-diff` can create multiple entries for one file.
  const fileMap = new Map<string, ParsedDiffFile>();
  for (const file of parsedFiles) {
    const path = file.to || file.from;
    if (!path) continue;

    const existingFile = fileMap.get(path);
    if (existingFile) {
      existingFile.additions += file.additions;
      existingFile.deletions += file.deletions;
      existingFile.chunks.push(...file.chunks);
    } else {
      fileMap.set(path, { ...file, chunks: [...file.chunks] });
    }
  }

  // After aggregation, sort the chunks within each file to ensure correct order.
  for (const file of fileMap.values()) {
    file.chunks.sort((a, b) => a.newStart - b.newStart);
  }

  const files = Array.from(fileMap.values());
  return { files, rawDiff };
};

export const useDiff = (
  repoId: string,
  prNumber: number,
) => {
  return useQuery<string, Error, { files: ParsedDiffFile[], rawDiff: string }>({
    queryKey: ['diff', repoId, prNumber],
    queryFn: () => fetchDiff(repoId, prNumber),
    select: cleanAndParseDiff,
    enabled: !!repoId && !!prNumber,
  });
};
